/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { ISystemCategoryOptionsFacade } from '../../../../../data-access/src/lib/domain/facades-models/sysque-system-category-options-facade-interface';
import { Observable, from, of } from 'rxjs';
import { LocalStorageService } from '../../../../../utils/src/lib/local-storage/local-storage.service';
import { Store } from '@ngrx/store';
import { BackendService } from '../../../../../utils/src';
import { SystemInfo } from '../../../../../data-access/src/lib/domain/system-info';
import { SysqueElectricalState } from '../../models/sysque-electrical-state';
import { GlobalConstants } from '../../constants/global-constants';
import { SysQueCategoryOperationsResult, SysQueSystemsOperationsResult } from '../../models/sysque-systems-operations';
import { TabDescription } from 'libs/sysque/toolbelt/data-access/src/lib/domain/tab-description';

@Injectable({
  providedIn: 'root',
})
export class SystemCategoryOptionsFacade implements ISystemCategoryOptionsFacade {
  constructor(
    private localStorageService: LocalStorageService,
    public store: Store,
    public backendService: BackendService,
  ) {}

  public requestSystemOptions(
    nameOfRequest: string,
    systemName: string,
    selectedSystemOrCategory: string,
    oldSystemName?: string
  ): Observable<SysQueSystemsOperationsResult> {
    let result: Observable<SysQueSystemsOperationsResult>;
    switch (nameOfRequest) {
      case GlobalConstants.CreateSystemRequestName:
        result = from<Promise<SysQueSystemsOperationsResult>>(
          this.backendService.messageHandler.sendRequestAsync(nameOfRequest, [systemName, selectedSystemOrCategory])
        );
        break;
      case GlobalConstants.RenameSystemRequestName:
        result = from<Promise<SysQueSystemsOperationsResult>>(
          this.backendService.messageHandler.sendRequestAsync(nameOfRequest, [
            systemName,
            oldSystemName,
            selectedSystemOrCategory,
          ])
        );
        break;
      case GlobalConstants.DuplicateSystemRequestName:
        result = from<Promise<SysQueSystemsOperationsResult>>(
          this.backendService.messageHandler.sendRequestAsync(nameOfRequest, [systemName, selectedSystemOrCategory])
        );
        break;
    }

    return result;
  }

  public requestCategoryOptions(
    requestName: string,
    newCategoryName: string,
    systemName: string,
    oldCategoryName?: string
  ): Observable<SysQueCategoryOperationsResult> {
    return requestName === GlobalConstants.CreateCategoryRequestName
      ? from<Promise<SysQueCategoryOperationsResult>>(
          this.backendService.messageHandler.sendRequestAsync(requestName, [newCategoryName, systemName])
        )
      : from<Promise<SysQueCategoryOperationsResult>>(
          this.backendService.messageHandler.sendRequestAsync(requestName, [
            newCategoryName,
            systemName,
            oldCategoryName,
          ])
        );
  }

  public closeWindow(): void {
    this.backendService.messageHandler.sendRequestAsync(GlobalConstants.OnCloseWindow);
  }

  public windowResize(height: number): void {
    this.backendService.messageHandler.sendRequestAsync(GlobalConstants.ChangeHeight, [height]);
  }

  public updateSystemInfo(systems: SystemInfo[]): void {
    const state = this.getSystemState();
    state.systemList = systems;
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, state);
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.MANAGE_MODE_SOURCE_SYSTEM_STATE, state);
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.MANAGE_MODE_DESTINATION_SYSTEM_STATE, state);
  }

  public getSystemState(): SysqueElectricalState {
    return this.localStorageService.getStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY) as SysqueElectricalState;
  }

  public updateStateService(categories: TabDescription[]): void {
    const stateService = this.getSystemState();
    stateService.categoryList = categories;
    this.localStorageService.updateDispatchLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, stateService);
  }
}
